<template>
  <a-card class="chart-container">
    <div class="parent-scope-item" v-if="parentTitle">{{ parentTitle }}</div>
    <div class="level-grid">
      <scopes-level-selector
        :parent-level="parentLevel"
        @update="onLevelUpdate"
      />
    </div>
    <div ref="chartContainer" class="chart-wrapper"></div>
    <div class="project-udc-counters">
      <scopes-project-stats :top-udc-id="currentId" />
    </div>
  </a-card>
</template>

<script setup>
  import { onMounted, ref, watch } from 'vue';
  import * as d3 from 'd3';
  import ScopesProjectStats from '@/components/projects/portfolio/components/ScopesProjectStats.vue';
  import ScopesLevelSelector from '@/components/projects/portfolio/components/ScopesLevelSelector.vue';

  const props = defineProps({
    data: {
      type: Object,
      required: true,
    },
  });

  const chartContainer = ref(null);
  const currentId = ref(0);
  const parentTitle = ref('');
  const parentLevel = ref(0); // Уровень УДК родительского (белого) круга. У цветных кругов уровень + 1

  // SVG dimensions
  const width = 800;
  const height = 800;
  const padding = 4;
  // R global
  const r_g = 283;
  // const r_g_max = 355;
  // R sibling
  const r_sib_max = 110;
  const r_sib_min = 50;
  // R satellites
  const r_sat_max = 36;
  const r_sat_min = 20;
  // R orbit for siblings
  const r_sib_o = r_sib_max + r_sat_max + r_sib_max;
  // R orbit for satellites
  // const r_sat_o = Math.round(r_sat_max * 1.1)

  const getMinMaxCount = (items) => {
    const counts = items.map((el) => el.count);
    return { min: Math.min(...counts), max: Math.max(...counts) };
  };

  const getSatStats = (items) => {
    const counts = items.reduce((acc, item) => {
      if (item.items && item.items.length) {
        const itemCounts = item.items.map((el) => el.count);
        acc = [...acc, ...itemCounts];
      }
      return acc;
    }, []);
    return {
      min: Math.min(...counts),
      max: Math.max(...counts),
      children_count: counts.length,
    };
  };

  function findItemById(data, id) {
    if (data.udcClassId === id) {
      return data;
    }

    if (data.items && Array.isArray(data.items)) {
      for (const item of data.items) {
        const result = findItemById(item, id);
        if (result) {
          return result;
        }
      }
    }
    return null;
  }

  function findParentIdByLevel(targetLevel, dataItem) {
    if (!dataItem) {
      const _dataItem = findItemById(props.data, currentId.value);
      return findParentIdByLevel(targetLevel, _dataItem);
    }

    if (dataItem.level - 1 === targetLevel)
      return dataItem.parentUdcClassId ?? 0;

    if (targetLevel >= dataItem.level) return 0;

    const parentId = dataItem.parentUdcClassId ?? 0;
    const parent = findItemById(props.data, parentId);
    return findParentIdByLevel(targetLevel, parent);
  }

  const createChart = (data) => {
    if (!chartContainer.value) return;

    // Clear previous chart
    d3.select(chartContainer.value).selectAll('*').remove();
    d3.selectAll('.ant-tooltip').remove();

    const parentItem = { ...data };
    delete parentItem.items;
    parentItem.r = r_g;
    parentItem.x = width / 2;
    parentItem.y = height / 2;

    const sortedItems = [...data.items].sort((a, b) => b.count - a.count);
    const { min, max } = getMinMaxCount(sortedItems);
    const a_0 = Math.random() * 2 * Math.PI;
    // Вычисляем расположение и размеры цветных кругов для науч. направлений
    const items = sortedItems.map((item, i) => {
      const a =
        i === 0
          ? 0
          : i === 1
          ? a_0
          : a_0 + (i - 1) * ((2 * Math.PI) / (sortedItems.length - 1));
      const r_min = r_sib_min + (r_sib_max - r_sib_min) / sortedItems.length;
      const r =
        max === min
          ? r_sib_max
          : Math.round(
              r_min + ((r_sib_max - r_min) * (item.count - min)) / (max - min)
            );
      const x = height / 2 + (i ? r_sib_o * Math.cos(a) : 0);
      const y = width / 2 + (i ? r_sib_o * Math.sin(a) : 0);
      return { ...item, r: r, a: a, x: x, y: y, main: true };
    });

    const {
      min: sat_min,
      max: sat_max,
      children_count: sat_number,
    } = getSatStats(items);
    // Вычисляем размеры и расположение кругов сателитов
    const satelliteData = items.map((item) => {
      if (!item.items || !item.items.length) return [];

      const a_0 = Math.random() * 2 * Math.PI;

      return item.items.map((sat, i) => {
        const a = a_0 + i * ((2 * Math.PI) / item.items.length);
        const r_min = r_sat_min + (r_sat_max - r_sat_min) / sat_number;
        const r =
          sat_min === sat_max
            ? r_sat_max
            : Math.round(
                r_min +
                  ((r_sat_max - r_min) * (sat.count - sat_min)) /
                    (sat_max - sat_min)
              );
        const x = item.r * Math.cos(a);
        const y = item.r * Math.sin(a);
        return { ...sat, r: r, a: a, x: x, y: y, main: false };
      });
    });

    const color = d3
      .scaleOrdinal()
      .domain(items.map((d) => d.notation))
      .range(d3.schemeCategory10);

    // Create SVG container
    const svg = d3
      .select(chartContainer.value)
      .append('svg')
      .attr('width', width)
      .attr('height', height)
      .attr('viewBox', [0, 0, width, height])
      .attr('style', 'max-width: 100%; height: auto;');

    // Create gradients for each scope circle (item)
    const defs = svg.append('defs');
    defs
      .selectAll('radialGradient')
      .data(items)
      .enter()
      .append('radialGradient')
      .attr('id', (d) => `rgrad-${d.notation}`)
      .attr('gradientUnits', 'objectBoundingBox')
      .attr('cx', '70%')
      .attr('cy', '30%')
      .attr('r', '100%')
      .each(function (d) {
        // Append gradient stops for each gradient
        d3.select(this)
          .append('stop')
          .attr('offset', '0%')
          .attr('stop-color', d3.color(color(d.notation)).brighter(1.5));
        d3.select(this)
          .append('stop')
          .attr('offset', '100%')
          .attr('stop-color', color(d.notation));
      });

    // Main Circle Shadow
    defs
      .append('filter')
      .attr('filterUnits', 'userSpaceOnUse')
      .attr('id', 'main-shadow')
      .attr('width', '250%')
      .attr('height', '250%')
      .append('feDropShadow')
      .attr('flood-opacity', '0.4')
      .attr('stdDeviation', 30);
    // Circle Shadow
    defs
      .append('filter')
      .attr('id', 'circle-shadow')
      .attr('width', '250%')
      .attr('height', '250%')
      .append('feDropShadow')
      .attr('dx', 5)
      .attr('dy', 5)
      .attr('flood-opacity', '0.5')
      .attr('stdDeviation', 6);
    // Satellite Circle Shadow
    defs
      .append('filter')
      .attr('id', 'satellite-shadow')
      .attr('width', '250%')
      .attr('height', '250%')
      .append('feDropShadow')
      .attr('dx', 3)
      .attr('dy', 3)
      .attr('flood-opacity', '0.5')
      .attr('stdDeviation', 3);

    // Parent scope circle
    svg
      .data([parentItem])
      .append('circle')
      .attr('class', 'node')
      .attr('cx', (d) => d.x)
      .attr('cy', (d) => d.y)
      .attr('r', (d) => d.r)
      .style('fill', '#fff')
      .style('stroke', '#fff')
      .style('filter', 'url(#main-shadow)');

    // Create nodes
    const node = svg
      .append('g')
      .attr('transform', `translate(${padding},${padding})`)
      .selectAll('circle')
      .data(items)
      .join('g')
      .attr('transform', (d) => `translate(${d.x},${d.y})`);

    // Add item circles
    node
      .append('circle')
      .attr('class', 'siblings-circle mouse-events')
      .attr('r', (d) => d.r)
      .attr('fill', (d) => `url(#rgrad-${d.notation})`)
      .attr('stroke', (d) => color(d.notation))
      .attr('stroke-width', 1)
      .style('filter', 'url(#circle-shadow)');

    // Center Caption text
    node
      .append('text')
      .attr('id', (d) => `scope-text-${d.notation}`)
      .attr('class', 'scope-text')
      .attr('x', 0)
      .attr('y', (d) => -(d.r * 0.15))
      .attr('dy', '0.3em')
      .attr('text-anchor', 'middle')
      .attr('cursor', 'default')
      .text((d) => `${d.notation}. ${d.caption}`)
      .style('fill', '#fff')
      .style('font-size', '16px')
      .style('font-weight', '600');

    // Bottom Count text
    node
      .append('text')
      .attr('class', 'count-text')
      .attr('x', 0)
      .attr('y', (d) => d.r * 0.6)
      .attr('dy', '0.3em')
      .attr('text-anchor', 'middle')
      .attr('cursor', 'default')
      .text((d) => d.count)
      .style('fill', '#fff')
      .style('font-size', '14px')
      .style('font-weight', '600');

    node.each(function (d, i) {
      const satellites = satelliteData[i];
      if (satellites.length === 0) return;

      const satNnode = d3
        .select(this)
        .append('g')
        .selectAll('circle')
        .data(satellites)
        .join('g')
        .attr('transform', (d) => `translate(${d.x},${d.y})`);

      satNnode
        .append('circle')
        .attr('r', (d) => d.r)
        .attr('fill', '#E9F3FA')
        .attr('stroke', '#999')
        .attr('stroke-width', 1)
        .style('filter', 'url(#satellite-shadow)');
      // Add Notation Text
      satNnode
        .append('text')
        .attr('class', 'satellite-notation')
        .attr('x', 0)
        .attr('y', (d) => -(d.r * 0.2))
        .attr('dy', '0.3em')
        .attr('text-anchor', 'middle')
        .attr('cursor', 'default')
        .text((d) => d.notation)
        .style('fill', '#2D3346')
        .style('font-size', '10px')
        .style('font-weight', '600');
      // Bottom Count text
      satNnode
        .append('text')
        .attr('class', 'count-text')
        .attr('x', 0)
        .attr('y', (d) => d.r * 0.5)
        .attr('dy', '0.3em')
        .attr('text-anchor', 'middle')
        .attr('cursor', 'default')
        .text((d) => d.count)
        .style('fill', '#718096')
        .style('font-size', '8px')
        .style('font-weight', '700');
    });

    const onClickEvent = (d) => {
      if (
        d.udcClassId === parentItem.udcClassId &&
        !parentItem.parentUdcClassId
      ) {
        currentId.value = 0;
      } else if (d.udcClassId === parentItem.udcClassId) {
        currentId.value = parentItem.parentUdcClassId;
      } else if (d.main && d.items) {
        currentId.value = d.udcClassId;
      } else if (!d.main) {
        currentId.value = d.parentUdcClassId;
      }
    };
    const onMouseOver = (element, d) => {
      element.attr('stroke-width', d.udcClassId === 0 ? 3 : 2);
      if (d.udcClassId !== 0) {
        tooltip.style('visibility', 'visible').html(`
        <strong>${d.notation}</strong><br>
          ${d.caption}
        `);
      }
    };
    const onMouseOut = (element) => {
      element.attr('stroke-width', 1);
      tooltip.style('visibility', 'hidden');
    };
    const onMouseMove = (event) => {
      tooltip
        .style('left', `${event.pageX + 10}px`)
        .style('top', `${event.pageY - 10}px`);
    };

    d3.selectAll('circle')
      .on('mouseover', function (event, d) {
        onMouseOver(d3.select(this), d);
      })
      .on('mouseout', function () {
        onMouseOut(d3.select(this));
      })
      .on('click', (event, d) => onClickEvent(d))
      .on('mousemove', (event) => onMouseMove(event));

    d3.selectAll('.scope-text,.satellite-notation,.count-text')
      .on('mouseover', function (event, d) {
        onMouseOver(d3.select(this), d);
      })
      .on('mouseout', function () {
        onMouseOut(d3.select(this));
      })
      .on('click', (event, d) => onClickEvent(d))
      .on('mousemove', (event) => onMouseMove(event));

    // Add tooltip
    const tooltip = d3
      .select('body')
      .append('div')
      .attr('class', 'ant-tooltip ant-tooltip-placement-top')
      .style('position', 'absolute')
      .style('visibility', 'hidden')
      .style('background', '#fff')
      .style('padding', '8px')
      .style('border-radius', '4px')
      .style('box-shadow', '0 2px 8px rgba(0,0,0,0.15)');

    svg.selectAll('.scope-text').each(function (d) {
      wrapText(d3.select(this), d.r * 1.8, d.r * 0.6);
    });

    svg.selectAll('.satellite-notation').each(function (d) {
      truncateText(d3.select(this), d.r * 1.8);
    });
  };

  function truncateText(textElement, maxWidth) {
    textElement.each(function () {
      const text = d3.select(this);
      let textContent = text.text();
      let length = textContent.length;

      // Reduce the text until it fits within the maxWidth
      while (text.node().getComputedTextLength() > maxWidth && length > 0) {
        textContent = textContent.slice(0, -1); // Remove one character
        text.text(textContent + '...'); // Add ellipsis
        length--;
      }
    });
  }

  function wrapText(text_element, width, height) {
    const lineHeight = 1.1; // ems
    const words = text_element.text().split(/\s+/);
    let line = [];

    const x = text_element.attr('x');
    const y = text_element.attr('y');
    let dy = 0;

    text_element.text(null); // Clear existing text

    let tspan = text_element
      .append('tspan')
      .attr('x', x)
      .attr('y', y)
      .attr('dx', '0.1em')
      .attr('dy', dy);

    let line_number = 0;
    for (const word of words) {
      line.push(word);
      tspan.text(line.join(' '));

      if (line_number === 1 || tspan.node().getComputedTextLength() > width) {
        line.pop(); // Remove the last word
        tspan.text(line.join(' ')); // Update the line
        // Check if remaining string has only one word and check if its length exceeds min width
        if (line.length === 1 && tspan.node().getComputedTextLength() > width) {
          truncateText(tspan, width);
        }

        dy += lineHeight * parseFloat(text_element.style('font-size')); // Move to the next line

        if (dy > height) {
          if (!tspan.text().includes('...')) {
            tspan.text(line.join(' ') + '...');
          }
          break;
        }

        line = [word]; // Start a new line

        // Add a new tspan for the next line
        tspan = text_element
          .append('tspan')
          .attr('x', x)
          .attr('y', y)
          .attr('dy', dy)
          .text(word);
      }
      line_number++;
    }
  }

  onMounted(() => {
    createChart(findItemById(props.data, currentId.value));
  });

  watch(currentId, (newValue) => {
    const newDataItem = findItemById(props.data, newValue);
    if (newDataItem) {
      parentLevel.value = newDataItem.level;
      parentTitle.value =
        newValue === 0 ? '' : `${newDataItem.notation}. ${newDataItem.caption}`;
      currentId.value = newValue;
      createChart(newDataItem);
    }
  });

  const onLevelUpdate = (newParenLevel) => {
    currentId.value = findParentIdByLevel(newParenLevel);
  };
</script>

<style scoped lang="scss">
@use "@/assets/styles/_colors.scss" as *;

  .chart-container {
    background-color: $color-bg-secondary-grey;
    width: 1130px;
    height: 814px;
    padding: 20px;
    margin: 40px 0;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .chart-wrapper {
    overflow: unset;
  }

  .parent-scope-item {
    top: 30px;
    left: 30px;
    position: absolute;
    font-family: Nunito Sans, sans-serif;
    font-weight: 700;
    font-size: 22px;
    line-height: 22px;
  }

  .level-grid {
    top: 30px;
    right: 30px;
    position: absolute;
    height: 198px;
    width: 54px;
  }

  .project-udc-counters {
    bottom: 30px;
    left: 30px;
    position: absolute;
    height: 102px;
  }
</style>
